/**
 * URL Changed view trigger
 * @module
 */

var _ = require('optimizely-client-fn');

var actionTypes = require('core/constants/action_types');
var di = require('core/di');
var enums = require('core/constants/enums');
var EventEmitter = require('core/event_emitter');
var Flux = require('core/flux');
var HistoryStore = di.get('stores/history');
var Logger = require('core/utils/logger');
var PromiseEnv = require('core/lib/promise').Promise;
var View = require('core/view');
var WindowEnv = require('core/env/window');

var URL_CHANGED = 'URLChanged';

/**
 * PluginManager registration method
 * @param {PluginManager} PluginManager
 */
module.exports = function(PluginManager:PluginManagerClass) {
  setUpObservers();
  PluginManager.registerViewTrigger(URL_CHANGED);
};

if (__TEST__) {
  module.exports.reset = tearDownObservers;
}

/**
 * Monitor the URL for changes, and emit a URLChanged event upon changes
 * @private
 */
function setUpObservers() {
  // Emit URLChanged when the URL changes.
  wrapPushState();
  wrapReplaceState();
  WindowEnv.addEventListener(
    'popstate',
    emitURLChangedViaPopState,
    false
  );
  WindowEnv.addEventListener(
    'hashchange',
    emitURLChangedViaHash,
    false
  );

  // Activate views when URLChanged is emitted.
  EventEmitter.on({
    filter: {
      type: 'viewTrigger',
      name: 'URLChanged',
    },
    handler: function () {
      View.getViewsAndActivate(enums.ViewActivationTypes.URLChanged);
    },
  });
}

/**
 * Stop monitoring the URL for changes. Currently only used to allow multiple test cases.
 * @private
 */
function tearDownObservers() {
  WindowEnv.getGlobal('history').pushState = HistoryStore.getOriginalPushState;
  WindowEnv.getGlobal('history').replaceState = HistoryStore.getOriginalReplaceState;
  WindowEnv.removeEventListener(
    'popstate',
    emitURLChangedViaPopState,
    false
  );
  WindowEnv.removeEventListener(
    'hashchange',
    emitURLChangedViaHash,
    false
  );
}

function wrapPushState() {
  function wrapper() {
    var returnValue = HistoryStore.getOriginalPushState().apply(this, arguments);

    PromiseEnv.resolve().then(function() {
      emitURLChanged('pushState');
    }).catch(function(e){
      Logger.error('Error while wrapPushState: ', e);
    });

    return returnValue;
  }

  Flux.dispatch(actionTypes.ENSURE_ORIGINAL_PUSHSTATE);
  WindowEnv.getGlobal('history').pushState = wrapper;
}

function wrapReplaceState() {
  function wrapper() {
    var returnValue = HistoryStore.getOriginalReplaceState().apply(this, arguments);

    PromiseEnv.resolve().then(function() {
      emitURLChanged('replaceState');
    }).catch(function(e){
      Logger.error('Error while wrapReplaceState: ', e);
    });

    return returnValue;
  }

  Flux.dispatch(actionTypes.ENSURE_ORIGINAL_REPLACESTATE);
  WindowEnv.getGlobal('history').replaceState = wrapper;
}

function emitURLChanged(source) {
  // Do this *last* because it signals the end of some unit tests.
  EventEmitter.emit({
    type: 'viewTrigger',
    name: 'URLChanged',
    data: {
      source: source,
      newURL: WindowEnv.getHref(),
    },
  }, true);
}

// Fix a reference to these functions so we can remove the event listeners as needed.
var emitURLChangedViaPopState = _.partial(emitURLChanged, 'popstate');
var emitURLChangedViaHash = _.partial(emitURLChanged, 'hashchange');
